<template>
  <v-dialog v-model="modal.show" width="450" persistent transition="dialog-bottom-transition">
    <v-card>
      <v-toolbar dark color="secondary">
        <v-toolbar-title>Add new {{ type }} badge</v-toolbar-title>
        <v-spacer />
        <v-btn icon dark @click="close()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-alert v-if="noImageAlert" type="warning">
        Please select a badge image
      </v-alert>
      <v-card-text class="mt-5">
        <ValidationObserver ref="obs" v-slot="{ invalid, handleSubmit }">
          <v-form ref="form" @submit.prevent="handleSubmit(submit)">
            <ValidationProvider name="title" rules="required" v-slot="{ errors, valid }">
              <v-text-field
                label="Title"
                v-model="modal.form.title"
                maxlength="100"
                :error-messages="errors"
                :success="valid"
              >
              </v-text-field>
            </ValidationProvider>
            <div class="d-flex">
              <image-upload-face label="Image" @image-changed="updateImage($event)"> </image-upload-face>
              <v-img
                v-if="modal.form.image"
                :style="{ maxWidth: '80px', maxHeight: '80px' }"
                :src="modal.form.image.dataUrl || modal.form.image"
                class="ml-5"
              >
              </v-img>
            </div>
            <div v-if="showDates" class="d-flex ml-n3">
              <v-menu
                ref="startDateMenu"
                v-model="startDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="130px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <ValidationProvider
                    mode="lazy"
                    name="start date"
                    rules="required"
                    v-slot="{ errors, valid }"
                    class="half-width mr-2"
                  >
                    <v-text-field
                      v-model="modal.form.startDate"
                      v-bind="attrs"
                      v-on="on"
                      label="Start Date (Incl.)"
                      prepend-icon="mdi-calendar"
                      readonly
                      clearable
                      :error-messages="errors"
                      :success="valid"
                    ></v-text-field>
                  </ValidationProvider>
                </template>

                <v-date-picker
                  ref="picker"
                  v-model="modal.form.startDate"
                  min="1920-01-01"
                  :allowed-dates="allowedDates"
                  @change="saveStartDate"
                ></v-date-picker>
              </v-menu>
              <v-menu
                ref="endDateMenu"
                v-model="endDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="130px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <ValidationProvider mode="lazy" name="end date" v-slot="{ errors, valid }" class="half-width">
                    <v-text-field
                      v-model="modal.form.endDate"
                      v-bind="attrs"
                      v-on="on"
                      label="End Date (Incl.)"
                      prepend-icon="mdi-calendar"
                      readonly
                      clearable
                      :error-messages="errors"
                      :success="valid"
                    />
                  </ValidationProvider>
                </template>

                <v-date-picker
                  ref="picker"
                  v-model="modal.form.endDate"
                  min="1920-01-01"
                  :allowed-dates="allowedDates"
                  @change="saveEndDate"
                ></v-date-picker>
              </v-menu>
              <v-tooltip right max-width="150">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon color="secondary" v-bind="attrs" v-on="on" class="ml-2">
                    mdi-information
                  </v-icon>
                </template>
                <span>
                  Please always set both dates where possible. If a badge is valid to earn forever like the 1st exercise
                  badge then the end date can be left blank.
                </span>
              </v-tooltip>
            </div>
            <ValidationProvider name="description" rules="required" v-slot="{ errors, valid }">
              <v-textarea
                label="Description"
                v-model="modal.form.description"
                rows="4"
                auto-grow
                :error-messages="errors"
                :success="valid"
              >
              </v-textarea>
            </ValidationProvider>
            <div v-if="showChallenge" class="d-flex">
              <ValidationProvider name="challenge" v-slot="{ errors, valid }" class="mr-2 full-width">
                <v-autocomplete
                  v-model="modal.form.blockChallenge"
                  :items="blockChallenges"
                  :loading="loadingBlockChallenges"
                  label="Challenge"
                  placeholder="Type to search"
                  clearable
                  :error-messages="errors"
                  :success="valid"
                />
              </ValidationProvider>
              <v-tooltip right max-width="150">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon color="secondary" v-bind="attrs" v-on="on" class="ml-2">
                    mdi-information
                  </v-icon>
                </template>
                <span>
                  Only set this if you want to limit who can earn this badge to a specific challenge. For multiple
                  challenges you will need to create another badge.
                </span>
              </v-tooltip>
            </div>
            <div v-if="showChallenge" class="d-flex">
              <ValidationProvider name="corporate challenge" v-slot="{ errors, valid }" class="mr-2 full-width">
                <v-autocomplete
                  v-model="modal.form.corporateChallenge"
                  :items="corporateChallenges"
                  :loading="loadingChallenges"
                  label="Corporate Challenge"
                  placeholder="Type to search"
                  clearable
                  :error-messages="errors"
                  :success="valid"
                />
              </ValidationProvider>
              <v-tooltip right max-width="150">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon color="secondary" v-bind="attrs" v-on="on" class="ml-2">
                    mdi-information
                  </v-icon>
                </template>
                <span>
                  Only set this if you want to limit who can earn this badge to a specific old challenge.
                </span>
              </v-tooltip>
            </div>
            <ValidationProvider
              v-if="showEvent"
              name="event"
              rules="required"
              v-slot="{ errors, valid }"
              class="mr-2 full-width"
            >
              <v-autocomplete
                v-model="modal.form.event"
                :items="events"
                :loading="loadingEvents"
                label="Event"
                placeholder="Type to search"
                clearable
                :error-messages="errors"
                :success="valid"
              />
            </ValidationProvider>
            <ValidationProvider
              v-if="showWeeksQualifier"
              name="weeks"
              rules="required"
              v-slot="{ errors, valid }"
              class="mr-2 full-width"
            >
              <v-text-field
                dense
                label="Weeks on track"
                v-model="modal.form.weeks"
                type="number"
                :min="1"
                :error-messages="errors"
                :success="valid"
              />
            </ValidationProvider>
            <div v-if="showQualifiers">
              <v-card class="mb-5">
                <div class="d-flex">
                  <v-card-title>
                    Qualifiers
                  </v-card-title>
                  <v-tooltip right max-width="150">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon color="secondary" v-bind="attrs" v-on="on" class="ml-2">
                        mdi-information
                      </v-icon>
                    </template>
                    <span>
                      Overlapping categories become redundant. [exercise: 5] and [any: 5] will be awarded to any client
                      that logs 5 dots;
                      <strong>not 10 dots</strong>
                    </span>
                  </v-tooltip>
                  <v-btn elevation="5" icon small color="secondary" class="mt-3 ml-auto mr-3" @click="addQualifier()">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </div>
                <v-container v-for="(qualifier, index) in modal.form.qualifiers" :key="index" class="d-flex py-0">
                  <ValidationProvider
                    name="activity"
                    rules="required"
                    v-slot="{ errors, valid }"
                    class="mr-2 half-width"
                  >
                    <v-autocomplete
                      dense
                      v-model="qualifier.activityType"
                      :items="activities"
                      label="Activity"
                      required
                      :error-messages="errors"
                      :success="valid"
                    />
                  </ValidationProvider>
                  <ValidationProvider name="quantity" rules="required" v-slot="{ errors, valid }" class="half-width">
                    <v-text-field
                      dense
                      label="Quantity"
                      v-model="qualifier.quantity"
                      type="number"
                      :error-messages="errors"
                      :success="valid"
                    />
                  </ValidationProvider>
                  <v-btn
                    v-if="modal.form.qualifiers.length > 1"
                    icon
                    small
                    @click="removeQualifier(index)"
                    color="blue-grey lighten-4"
                  >
                    <v-icon> mdi-close </v-icon>
                  </v-btn>
                </v-container>
              </v-card>
            </div>
            <!-- Must always be 34 characters long -->
            <ValidationProvider name="sendgridTemplateId" rules="sendgridTemplateId" v-slot="{ errors, valid }">
              <v-text-field
                label="Sendgrid Template Id"
                v-model="modal.form.sendgridTemplateId"
                counter="34"
                :error-messages="errors"
                :success="valid"
              >
              </v-text-field>
            </ValidationProvider>
            <div class="d-flex">
              <v-checkbox v-if="showVisible" v-model="modal.form.visible" label="Visible"> </v-checkbox>
              <v-tooltip right max-width="150">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon color="secondary" v-bind="attrs" v-on="on" class="ml-2">
                    mdi-information
                  </v-icon>
                </template>
                <span>
                  Enabled badges that are
                  <strong>not visible can still be earned</strong>; and will display on the client's list once they are
                  earned.
                </span>
              </v-tooltip>
            </div>
            <v-checkbox v-if="showEnabled" v-model="modal.form.enabled" label="Enabled" class="mt-0"> </v-checkbox>
            <v-card-actions>
              <v-spacer />
              <v-btn color="secondary" type="submit" :disabled="invalid" :loading="buttonLoading">Save</v-btn>
            </v-card-actions>
          </v-form>
        </ValidationObserver>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  SIMPLE_BLOCK_CHALLENGES_QUERY,
  SIMPLE_CORPORATE_CHALLENGES_QUERY,
  SIMPLE_EVENTS_QUERY,
  UPSERT_COACH_BADGE_MUTATION,
  UPSERT_CHALLENGE_BADGE_MUTATION,
  UPSERT_CONSISTENCY_BADGE_MUTATION,
  UPSERT_EVENT_BADGE_MUTATION,
  UPSERT_CUSTOM_BADGE_MUTATION,
} from "@/constants/graphql/badges/badge";
import { ACTIVITIES_QUERY } from "@/constants/graphql/goal/goal";
import { dataURLtoBlob, blobToFile } from "@/lib/file";
import ImageUploadFace from "@/components/shared/ImageUploadFace";
import BadgeDisplayMixin from "./mixins/BadgeDisplayMixin";

export default {
  name: "BadgeDialog",
  props: {
    type: {
      type: String,
      required: true,
    },
    badgeModal: {
      type: Object,
      default: () => {
        return {
          show: false,
        };
      },
    },
    allowedDates: {
      type: Function,
      default: () => {
        return true;
      }
    }
  },
  mixins: [BadgeDisplayMixin],
  components: {
    ImageUploadFace,
  },
  data() {
    return {
      modal: null,
      noImageAlert: false,
      buttonLoading: false,
      startDateMenu: false,
      endDateMenu: false,
      loadingChallenges: true,
      loadingBlockChallenges: true,
      loadingEvents: true,
    };
  },
  created() {
    this.modal = this.badgeModal;
  },
  computed: {
    activities() {
      if (this.activitiesList) {
        let activities = this.activitiesList;
        if (
          !activities.includes("any") &&
          !this.modal.form.qualifiers.some((qualifier) => qualifier.activityType == "any")
        ) {
          activities.unshift("any");
        }

        return activities;
      }

      return [];
    },
  },
  methods: {
    close() {
      this.$emit("close");
    },
    updateImage(image) {
      this.modal.form.image = image;
      if (image) {
        this.noImageAlert = false;
        this.modal.form.imageChanged = true;
      }
    },
    getImageFile(image) {
      if (this.modal.form.imageChanged) {
        let blob = dataURLtoBlob(image.dataUrl);
        return blobToFile(blob);
      } else {
        return image;
      }
    },
    addQualifier() {
      this.modal.form.qualifiers.push({
        activityType: null,
        quantity: null,
      });
    },
    removeQualifier(index) {
      this.modal.form.qualifiers.splice(index, 1);
    },
    saveStartDate(date) {
      this.$refs.startDateMenu.save(date);
    },
    saveEndDate(date) {
      this.$refs.endDateMenu.save(date);
    },
    submit() {
      if (!this.modal.form.image) {
        this.noImageAlert = true;
      } else {
        this.noImageAlert = false;
        this.buttonLoading = true;

        let input = {
          id: this.modal.form.id,
          title: this.modal.form.title,
          description: this.modal.form.description,
          image: this.modal.form.imageChanged ? this.getImageFile(this.modal.form.image) : null,
          imageChanged: this.modal.form.imageChanged,
          enabled: this.modal.form.enabled,
          sendgridTemplateId: this.modal.form.sendgridTemplateId,
        };

        switch (this.type) {
          case "coach":
            this.upsertCoachBadge(input);
            break;

          case "challenge":
            this.upsertChallengeBadge(input);
            break;

          case "consistency":
            this.upsertConsistencyBadge(input);
            break;

          case "event":
            this.upsertEventBadge(input);
            break;

          case "custom":
            this.upsertCustomBadge(input);
            break;
        }
      }
    },
    upsertCoachBadge(input) {
      this.$apollo
        .mutate({
          mutation: UPSERT_COACH_BADGE_MUTATION,
          variables: {
            input: input,
          },
          refetchQueries: ["adminBadgesQuery"],
        })
        .then((response) => {
          if (response.data) {
            this.successfulUpsert();
          }
        });
    },
    upsertChallengeBadge(input) {
      let challengeInput = {
        startDate: this.modal.form.startDate,
        endDate: this.modal.form.endDate,
        blockChallengeId: this.modal.form.blockChallenge ?? null,
        corporateChallengeId: this.modal.form.corporateChallenge ?? null,
        qualifiers: this.modal.form.qualifiers,
        visible: this.modal.form.visible,
      };

      this.$apollo
        .mutate({
          mutation: UPSERT_CHALLENGE_BADGE_MUTATION,
          variables: {
            input: input,
            challengeInput: challengeInput,
          },
          refetchQueries: ["adminBadgesQuery"],
        })
        .then((response) => {
          if (response.data) {
            this.successfulUpsert();
          }
        });
    },
    upsertConsistencyBadge(input) {
      let consistencyInput = {
        startDate: this.modal.form.startDate,
        endDate: this.modal.form.endDate,
        blockChallengeId: this.modal.form.blockChallenge ?? null,
        corporateChallengeId: this.modal.form.corporateChallenge ?? null,
        weeks: this.modal.form.weeks,
        visible: this.modal.form.visible,
      };

      this.$apollo
        .mutate({
          mutation: UPSERT_CONSISTENCY_BADGE_MUTATION,
          variables: {
            input: input,
            consistencyInput: consistencyInput,
          },
          refetchQueries: ["adminBadgesQuery"],
        })
        .then((response) => {
          if (response.data) {
            this.successfulUpsert();
          }
        });
    },
    upsertEventBadge(input) {
      let eventInput = {
        startDate: this.modal.form.startDate,
        endDate: this.modal.form.endDate,
        blockChallengeId: this.modal.form.blockChallenge ?? null,
        corporateChallengeId: this.modal.form.corporateChallenge ?? null,
        eventId: this.modal.form.event,
        visible: this.modal.form.visible,
      };

      this.$apollo
        .mutate({
          mutation: UPSERT_EVENT_BADGE_MUTATION,
          variables: {
            input: input,
            eventInput: eventInput,
          },
          refetchQueries: ["adminBadgesQuery"],
        })
        .then((response) => {
          if (response.data) {
            this.successfulUpsert();
          }
        });
    },
    upsertCustomBadge(input) {
      this.$apollo
        .mutate({
          mutation: UPSERT_CUSTOM_BADGE_MUTATION,
          variables: {
            input: input,
          },
          refetchQueries: ["adminBadgesQuery"],
        })
        .then((response) => {
          if (response.data) {
            this.successfulUpsert();
          }
        });
    },
    successfulUpsert() {
      this.modal.show = false;
      this.buttonLoading = false;
    },
  },
  apollo: {
    activitiesList: {
      query: ACTIVITIES_QUERY,
      fetchPolicy: "cache-and-network",
      update(data) {
        return data.goalActivities;
      },
      skip() {
        return !this.showChallenge;
      },
    },
    corporateChallenges: {
      query: SIMPLE_CORPORATE_CHALLENGES_QUERY,
      fetchPolicy: "cache-and-network",
      update(data) {
        let challenges = data.corporateChallenges.map((challenge) => {
          let month = this.$moment(challenge.startDate).format("MMM");

          return {
            text: `${month} - ${challenge.title}`,
            value: challenge.id,
          };
        });

        this.loadingChallenges = false;
        return challenges;
      },
      skip() {
        return !this.showChallenge;
      },
    },
    blockChallenges: {
      query: SIMPLE_BLOCK_CHALLENGES_QUERY,
      fetchPolicy: "cache-and-network",
      update(data) {
        let workoutChallenges = data.workoutBlocks?.edges.map((edge) => {
          let month = this.$moment(edge.node.startDate).format("MMM");

          return {
            text: `${month} - ${edge.node.title}`,
            value: atob(edge.node.id).split(":")[1],
          };
        });

        this.loadingBlockChallenges = false;
        return workoutChallenges;
      },
      skip() {
        return !this.showChallenge;
      },
    },
    events: {
      query: SIMPLE_EVENTS_QUERY,
      fetchPolicy: "cache-and-network",
      update(data) {
        data.adminEvents.sort((a, b) => (a.scheduledDate < b.scheduledDate ? 1 : -1));

        let events = data.adminEvents.map((event) => {
          let date = this.$moment(event.scheduledDate).format("YYYY-MM-DD");
          let time = this.$moment(event.startTime, "HH:mm:ss").format("HH:mm");

          return {
            text: `${date} ${time}: ${event.name}`,
            value: parseInt(event.id),
          };
        });

        this.loadingEvents = false;
        return events;
      },
      skip() {
        return !this.showEvent;
      },
    },
  },
};
</script>

<style scoped>
.half-width {
  width: 50%;
}
.full-width {
  width: 100%;
}
</style>
